import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Button } from "@/components/ui";
import { toast } from "sonner";
import { FileDown, Stethoscope, Code2, ShieldCheck, Headphones } from "lucide-react";

const GROUPS = [
  { label: "Clinical & Delivery", icon: Stethoscope, roles: [
    { key: "clinical-director", title: "Clinical / Medical Director", meta: "Fractional · $150–$300/hr" },
    { key: "care-manager", title: "Care Manager (RN/LCSW/LCAS)", meta: "Contract→FTE · $75k–$95k" },
    { key: "ur-nurse", title: "Utilization Review Nurse (RN)", meta: "Contract→FTE · $80k–$100k" },
    { key: "hedis-analyst", title: "Quality / HEDIS Analyst", meta: "Contract/FTE · $75k–$105k" },
  ]},
  { label: "Engineering & Product", icon: Code2, roles: [
    { key: "lead-engineer", title: "Lead / Founding Full-Stack Engineer", meta: "FTE · $130k–$180k" },
    { key: "ai-engineer", title: "AI Engineer", meta: "Contract→FTE · $140k–$200k" },
    { key: "implementation-consultant", title: "Implementation / Solutions Consultant", meta: "Contract→FTE · $90k–$130k" },
    { key: "data-edi-analyst", title: "Data / EDI Analyst", meta: "Contract→FTE · $80k–$110k" },
  ]},
  { label: "Compliance, Security & Finance", icon: ShieldCheck, roles: [
    { key: "compliance-officer", title: "Compliance / Privacy Officer", meta: "Fractional · $100–$200/hr" },
    { key: "vciso", title: "Virtual CISO (vCISO)", meta: "Retainer · $3k–$8k/mo" },
    { key: "controller", title: "Controller / Bookkeeper", meta: "Fractional · $40–$150/hr" },
  ]},
  { label: "Go-To-Market & Support", icon: Headphones, roles: [
    { key: "sales-bd", title: "Sales / Business Development", meta: "FTE · $70k–$120k + comm." },
    { key: "customer-success", title: "Customer Success / Account Manager", meta: "FTE · $70k–$100k + var." },
    { key: "support-agent", title: "Contact-Center / Support Agent", meta: "Onshore · $18–$28/hr" },
  ]},
];

export default function JobDescriptions() {
  const [busy, setBusy] = useState("");
  const download = async (key, title) => {
    setBusy(key);
    try {
      const res = await api.get(`/docs/jd/${key}/pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = `GoMCO-JD-${key}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error("Download failed"); } finally { setBusy(""); }
  };
  return (
    <div className="vc-fade-up" data-testid="job-descriptions">
      <PageHeader title="Role Job Descriptions" subtitle="A ready-to-post JD PDF for every role — includes summary, responsibilities, requirements, pay, and how to apply. Download and paste straight into LinkedIn, Indeed, Wellfound, Upwork, or your ATS." />
      <div className="grid grid-cols-1 gap-5">
        {GROUPS.map((g) => (
          <Card key={g.label} className="p-6" data-testid={`jd-group-${g.label.split(" ")[0].toLowerCase()}`}>
            <div className="flex items-center gap-2.5 mb-4">
              <span className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center"><g.icon className="h-5 w-5 text-brand-700" /></span>
              <h2 className="font-display text-lg font-bold">{g.label}</h2>
            </div>
            <div className="space-y-2.5">
              {g.roles.map((r) => (
                <div key={r.key} className="flex items-center gap-3 rounded-md border border-border p-3.5" data-testid={`jd-row-${r.key}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm text-foreground">{r.title}</p>
                    <p className="text-xs text-muted-foreground">{r.meta}</p>
                  </div>
                  <Button size="sm" variant="outline" disabled={busy === r.key} onClick={() => download(r.key, r.title)} data-testid={`jd-pdf-${r.key}`}>
                    <FileDown className="h-3.5 w-3.5" /> {busy === r.key ? "…" : "Download JD"}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
